import { Option } from 'effect';
import * as Atom from 'effect/unstable/reactivity/Atom';
import * as AsyncResult from 'effect/unstable/reactivity/AsyncResult';

/**
 * Converts a Result to an Option, extracting the success value or returning None.
 * This is useful for atoms that need to work with optional data from Result types.
 *
 * @param result - The Result to convert
 * @returns Option.Some with the success value, or Option.None for Initial/Failure states
 *
 * @example
 * ```ts
 * const decodedTokenOption = resultToOption(decodedTokenResult);
 * ```
 */
export function resultToOption<A, E>(result: AsyncResult.AsyncResult<A, E>): Option.Option<A> {
  return AsyncResult.match(result, {
    onInitial: () => Option.none(),
    onFailure: () => Option.none(),
    onSuccess: (success) => Option.some(success.value),
  });
}

/**
 * Flattens a Result<Option<A>> to Option<A>.
 * Useful when the Effect already returns Option, and we want to avoid Option<Option<A>>.
 *
 * @param result - The Result containing an Option to flatten
 * @returns Flattened Option
 *
 * @example
 * ```ts
 * const tokenOption = resultOptionToOption(tokenResult);
 * ```
 */
export function resultOptionToOption<A, E>(
  result: AsyncResult.AsyncResult<Option.Option<A>, E>
): Option.Option<A> {
  return AsyncResult.match(result, {
    onInitial: () => Option.none(),
    onFailure: () => Option.none(),
    onSuccess: (success) => success.value, // Already an Option, no need to wrap
  });
}

/**
 * Creates a mapped atom that converts a Result atom to an Option atom.
 * This is a common pattern for extracting values from Result atoms.
 *
 * @param atom - The Result atom to map
 * @returns A new atom with Option type
 *
 * @example
 * ```ts
 * const tokenValueAtom = mapResultToOption(tokenResultAtom);
 * ```
 */
export function mapResultToOption<A, E>(
  atom: Atom.Atom<AsyncResult.AsyncResult<A, E>>
): Atom.Atom<Option.Option<A>> {
  return Atom.map(atom, resultToOption);
}

/**
 * Creates a mapped atom that flattens a Result<Option<A>> atom to Option<A> atom.
 * Prevents nested Option types when the underlying Effect already returns Option.
 *
 * @param atom - The Result<Option<A>> atom to flatten
 * @returns A new atom with flattened Option type
 *
 * @example
 * ```ts
 * const tokenAtom = mapResultOptionToOption(tokenResultOptionAtom);
 * ```
 */
export function mapResultOptionToOption<A, E>(
  atom: Atom.Atom<AsyncResult.AsyncResult<Option.Option<A>, E>>
): Atom.Atom<Option.Option<A>> {
  return Atom.map(atom, resultOptionToOption);
}

/**
 * Creates a mapped atom that extracts a nullable value from a Result atom.
 * Useful when you need to work with null instead of Option.
 *
 * @param atom - The Result atom to map
 * @returns A new atom with nullable type
 *
 * @example
 * ```ts
 * const tokenAtom = mapResultToNullable(tokenResultAtom);
 * ```
 */
export function mapResultToNullable<A, E>(
  atom: Atom.Atom<AsyncResult.AsyncResult<A, E>>
): Atom.Atom<A | null> {
  return Atom.map(atom, (result) => Option.getOrNull(resultToOption(result)));
}

/**
 * Creates a mapped atom that extracts a property from a Result atom's value.
 * Returns null if the Result is not successful or the property is undefined.
 *
 * @param atom - The Result atom to map
 * @param prop - The property key to extract
 * @returns A new atom with the property value or null
 *
 * @example
 * ```ts
 * const emailAtom = mapResultProperty(decodedTokenAtom, 'username');
 * ```
 */
export function mapResultProperty<A, E, K extends keyof A>(
  atom: Atom.Atom<AsyncResult.AsyncResult<A, E>>,
  prop: K
): Atom.Atom<A[K] | null> {
  return Atom.map(atom, (result) => {
    const option = resultToOption(result);
    const nullable = Option.getOrNull(option);
    return nullable?.[prop] ?? null;
  });
}

/**
 * Creates a mapped atom that extracts a property from a Result<Option<A>> atom's value.
 * Returns null if the Result is not successful, Option is None, or the property is undefined.
 *
 * @param atom - The Result<Option<A>> atom to map
 * @param prop - The property key to extract
 * @returns A new atom with the property value or null
 *
 * @example
 * ```ts
 * const emailAtom = mapResultOptionProperty(decodedTokenAtom, 'username');
 * ```
 */
export function mapResultOptionProperty<A, E, K extends keyof A>(
  atom: Atom.Atom<AsyncResult.AsyncResult<Option.Option<A>, E>>,
  prop: K
): Atom.Atom<A[K] | null> {
  return Atom.map(atom, (result) => {
    const option = resultOptionToOption(result);
    const nullable = Option.getOrNull(option);
    return nullable?.[prop] ?? null;
  });
}

/**
 * Creates a mapped atom that extracts a nullable value from an Option atom.
 * Useful when you need to work with null instead of Option.
 *
 * @param atom - The Option atom to map
 * @returns A new atom with nullable type
 *
 * @example
 * ```ts
 * const tokenAtom = mapOptionToNullable(tokenOptionAtom);
 * ```
 */
export function mapOptionToNullable<A>(atom: Atom.Atom<Option.Option<A>>): Atom.Atom<A | null> {
  return Atom.map(atom, Option.getOrNull);
}

/**
 * Creates a mapped atom that extracts a property from an Option atom's value.
 * Returns null if the Option is None or the property is undefined.
 *
 * @param atom - The Option atom to map
 * @param prop - The property key to extract
 * @returns A new atom with the property value or null
 *
 * @example
 * ```ts
 * const emailAtom = mapOptionProperty(decodedTokenAtom, 'username');
 * ```
 */
export function mapOptionProperty<A, K extends keyof A>(
  atom: Atom.Atom<Option.Option<A>>,
  prop: K
): Atom.Atom<A[K] | null> {
  return Atom.map(atom, (option) => {
    const nullable = Option.getOrNull(option);
    return nullable?.[prop] ?? null;
  });
}
